import {
  ref,
  onMounted,
  onActivated,
  watch,
  nextTick,
  type Ref,
  type ComputedRef,
} from 'vue';

export interface UseHorizontalScrollToActiveOptions {
  /** CSS class name to identify the active element (default: 'js-active-item') */
  activeClass?: string;
  /** Width of each element in pixels (default: 100, so center offset is 50) */
  elementWidth?: number;
  items?: Ref<unknown>;
}

/**
 * Sets up horizontal scrolling to active element in a scrollable container.
 * Uses manual scroll calculation to avoid vertical scrolling that scrollIntoView causes.
 *
 * @param scrollableContainerRef - Ref or ComputedRef to the scrollable container element
 * @param itemsRef - Ref array of elements to search for the active one
 * @param options - Configuration options
 * @returns Object with scroll function and shiftSize ref
 */
export function useHorizontalScrollToActive(
  scrollableContainerRef:
    | Ref<HTMLElement | null>
    | ComputedRef<HTMLElement | null>,
  itemsRef: Ref<Array<HTMLElement> | null>,
  options: UseHorizontalScrollToActiveOptions = {},
) {
  const { activeClass = 'js-active-item', elementWidth = 100, items } = options;

  const shiftSize = ref(0);

  const getActiveElement = () => {
    return itemsRef.value?.find(item => item.classList.contains(activeClass));
  };
  const getActiveElementIndex = () => {
    return itemsRef.value?.findIndex(item =>
      item.classList.contains(activeClass),
    );
  };

  /** scrollIntoView don't fit here, so we need to calculate shift for the scroll to active element manually */
  const calculateShiftSize = () => {
    const activeElement = getActiveElement();
    if (!activeElement) {
      return 0;
    }

    const container = scrollableContainerRef.value;
    if (!container) {
      return 0;
    }

    const itemsCount = itemsRef.value?.length || 0;
    if (itemsCount === 0) {
      return 0;
    }

    const activeElementIndex = getActiveElementIndex();
    if (activeElementIndex === -1 || activeElementIndex === undefined) {
      return 0;
    }

    /** Calculate the position of the active element's center based on items count and elementWidth */
    const activeElementCenterPoint =
      activeElementIndex * elementWidth + elementWidth / 2;

    /** Get container's visible width */
    const containerRect = container.getBoundingClientRect();
    const containerVisibleWidth = containerRect.width;

    /** Calculate the center point of the container's visible area */
    const containerCenterPoint = containerVisibleWidth / 2;

    /** This gives exact shift size for scroll to place active element to the center of the container */
    return activeElementCenterPoint - containerCenterPoint;
  };

  /** Using scroll function, because scrollIntoView scrolls page vertically, but we don't need it */
  const scrollToActive = () => {
    const container = scrollableContainerRef.value;
    if (container) {
      shiftSize.value = calculateShiftSize();
      container.scroll({ left: shiftSize.value });
    }
  };

  watch(
    () => items?.value,
    () => {
      nextTick(() => {
        scrollToActive();
      });
    },
  );

  onMounted(() => {
    scrollToActive();
  });

  onActivated(() => {
    scrollToActive();
  });
}
